import degreeToRadian from "common/utils/degreeToRadian";
import { ANGLE_EPSILON } from "./core";
import { intersectPlaneZ0 } from "./intersect";
import { planeCopy, planeNormal, planeProject, planeRotateAroundNormal } from "./plane";
import type { Plane } from "./planeTypes";
import { polyLine2MinBoundingRect } from "./polyline2MinimalBB";
import { polyLine2New } from "./polyLine2Types";
import { vec2Dot, vec2Len, vec2Normalize } from "./vec2";
import { vec3Cross, vec3Dot } from "./vec3";

export enum UVGeneration {
  NONE = 0,
  GLOBAL_RECT_U = 1 << 0,
  GLOBAL_RECT_V = 1 << 1,
  INVERT = 1 << 2,
  GLOBAL_RECT_UV = GLOBAL_RECT_U | GLOBAL_RECT_V,
  LONGITUDINAL_U = 1 << 3,
}

export interface UVTransformation {
  uTranslation: number;
  vTranslation: number;
  rotationDegrees: number;
  isMirrored?: boolean;
}

export const uvIdentityXForm: UVTransformation = {
  uTranslation: 0,
  vTranslation: 0,
  rotationDegrees: 0,
};

export function uvTranformationNew(txfm?: Partial<UVTransformation>): UVTransformation {
  return {
    ...uvIdentityXForm,
    ...txfm,
  };
}

export function applyUVs(
  uvs: Float32Array | Float64Array | number[],
  verts: ArrayLike<number>,
  vertsPlane: Plane,
  uvGen: UVGeneration,
  xform: UVTransformation = uvIdentityXForm,
  uvsStartIndex: number = 0,
  uvsEndIndex: number = uvs.length,
  vertsStartIndex: number = 0,
): Plane | null {
  console.assert((uvs.length - uvsStartIndex) / 2 <= (verts.length - vertsStartIndex) / 3);
  if (verts.length - vertsStartIndex < 3) {
    return null;
  }

  const uvPlane = planeCopy(vertsPlane);
  if (uvGen & UVGeneration.GLOBAL_RECT_UV) {
    const z0Intersection = intersectPlaneZ0(vertsPlane);
    if (z0Intersection) {
      const uX = z0Intersection.dirX;
      const uY = z0Intersection.dirY;
      const uZ = z0Intersection.dirZ;
      const [nX, nY, nZ] = planeNormal(vertsPlane);
      let [vX, vY, vZ] = vec3Cross(nX, nY, nZ, uX, uY, uZ); // Will be normalized
      // The V vector must always point up because material patterns are designed that way.
      if (vZ < 0) {
        [vX, vY, vZ] = [-vX, -vY, -vZ];
      }
      for (let i = uvsStartIndex, v = vertsStartIndex, l = uvsEndIndex; i < l; i += 2, v += 3) {
        const dX = verts[v] - z0Intersection.originX;
        const dY = verts[v + 1] - z0Intersection.originY;
        const dZ = verts[v + 2] - z0Intersection.originZ;
        if (uvGen & UVGeneration.GLOBAL_RECT_U) {
          uvs[i] = vec3Dot(uX, uY, uZ, dX, dY, dZ);
        }

        if (uvGen & UVGeneration.GLOBAL_RECT_V) {
          uvs[i + 1] = vec3Dot(vX, vY, vZ, dX, dY, dZ);
        }
      }
      uvPlane.originX = z0Intersection.originX;
      uvPlane.originY = z0Intersection.originY;
      uvPlane.originZ = z0Intersection.originZ;
      uvPlane.uX = uX;
      uvPlane.uY = uY;
      uvPlane.uZ = uZ;
      uvPlane.vX = vX;
      uvPlane.vY = vY;
      uvPlane.vZ = vZ;
    } else {
      for (let i = uvsStartIndex, v = vertsStartIndex, l = uvsEndIndex; i < l; i += 2, v += 3) {
        if (uvGen & UVGeneration.GLOBAL_RECT_U) {
          uvs[i] = verts[v];
        }

        if (uvGen & UVGeneration.GLOBAL_RECT_V) {
          uvs[i + 1] = verts[v + 1];
        }
      }
      uvPlane.originX = 0;
      uvPlane.originY = 0;
      uvPlane.originZ = verts[vertsStartIndex + 2];
      uvPlane.uX = 1;
      uvPlane.uY = 0;
      uvPlane.uZ = 0;
      uvPlane.vX = 0;
      uvPlane.vY = 1;
      uvPlane.vZ = 0;
    }
  } else if (uvGen & UVGeneration.LONGITUDINAL_U) {
    const numVertices = uvsEndIndex - uvsStartIndex || (verts.length / 3) * 2;
    const uvPolyline = polyLine2New(numVertices);
    for (let i2 = 0, i3 = vertsStartIndex, l = numVertices * 2; i2 < l; i2 += 2, i3 += 3) {
      const [x, y] = planeProject(uvPlane, verts[i3], verts[i3 + 1], verts[i3 + 2]);
      uvPolyline[i2] = x;
      uvPolyline[i2 + 1] = y;
    }
    const minAreaBoundingRect = polyLine2MinBoundingRect(uvPolyline);
    if (minAreaBoundingRect) {
      const uLen = vec2Len(minAreaBoundingRect.u[0], minAreaBoundingRect.u[1]);
      const vLen = vec2Len(minAreaBoundingRect.v[0], minAreaBoundingRect.v[1]);
      let ux = uLen > vLen ? minAreaBoundingRect.u[0] : minAreaBoundingRect.v[0];
      let uy = uLen > vLen ? minAreaBoundingRect.u[1] : minAreaBoundingRect.v[1];
      let vx = uLen > vLen ? minAreaBoundingRect.v[0] : minAreaBoundingRect.u[0];
      let vy = uLen > vLen ? minAreaBoundingRect.v[1] : minAreaBoundingRect.u[1];
      const minAreaBoundingRectVDirZ = uvPlane.uZ * vx + uvPlane.vZ * vy;
      if (minAreaBoundingRectVDirZ < 0) {
        ux *= -1;
        uy *= -1;
        vx *= -1;
        vy *= -1;
      }
      [ux, uy] = vec2Normalize(ux, uy);
      [vx, vy] = vec2Normalize(vx, vy);

      // Centroid would be another representation-invariant candidate for origin.
      // const [ox, oy] = polyline2PointCloudCentroid(uvPolyline);
      // We use the projected global origin instead to keep similar behavior to
      // UVGeneration.GLOBAL_RECT_UV.

      const [ox, oy] = planeProject(uvPlane, 0, 0, 0);

      for (let iout = uvsStartIndex, i = 0; iout < uvsEndIndex; i += 2, iout += 2) {
        const dX = uvPolyline[i] - ox;
        const dY = uvPolyline[i + 1] - oy;
        uvs[iout] = vec2Dot(ux, uy, dX, dY);
        uvs[iout + 1] = vec2Dot(vx, vy, dX, dY);
      }

      const planeUx = uvPlane.uX * ux + uvPlane.vX * uy;
      const planeUy = uvPlane.uY * ux + uvPlane.vY * uy;
      const planeUz = uvPlane.uZ * ux + uvPlane.vZ * uy;
      const planeVx = uvPlane.uX * vx + uvPlane.vX * vy;
      const planeVy = uvPlane.uY * vx + uvPlane.vY * vy;
      const planeVz = uvPlane.uZ * vx + uvPlane.vZ * vy;
      uvPlane.originX += uvPlane.uX * ox + uvPlane.vX * oy;
      uvPlane.originY += uvPlane.uY * ox + uvPlane.vY * oy;
      uvPlane.originZ += uvPlane.uZ * ox + uvPlane.vZ * oy;
      uvPlane.uX = planeUx;
      uvPlane.uY = planeUy;
      uvPlane.uZ = planeUz;
      uvPlane.vX = planeVx;
      uvPlane.vY = planeVy;
      uvPlane.vZ = planeVz;
    }
  }

  if (uvGen & UVGeneration.INVERT) {
    for (let i = uvsStartIndex, l = uvsEndIndex; i < l; i += 2) {
      const tmp = uvs[i];
      uvs[i] = uvs[i + 1];
      uvs[i + 1] = tmp;
    }
    [uvPlane.uX, uvPlane.vX] = [uvPlane.vX, uvPlane.uX];
    [uvPlane.uY, uvPlane.vY] = [uvPlane.vY, uvPlane.uY];
    [uvPlane.uZ, uvPlane.vZ] = [uvPlane.vZ, uvPlane.uZ];
  }

  for (let i = uvsStartIndex, l = uvsEndIndex; i < l; i += 2) {
    uvs[i] -= xform.uTranslation ?? 0;
    uvs[i + 1] -= xform.vTranslation ?? 0;
  }
  uvPlane.originX += uvPlane.uX * (xform.uTranslation ?? 0);
  uvPlane.originY += uvPlane.uY * (xform.uTranslation ?? 0);
  uvPlane.originZ += uvPlane.uZ * (xform.uTranslation ?? 0);
  uvPlane.originX += uvPlane.vX * (xform.vTranslation ?? 0);
  uvPlane.originY += uvPlane.vY * (xform.vTranslation ?? 0);
  uvPlane.originZ += uvPlane.vZ * (xform.vTranslation ?? 0);

  const actualRotation = xform?.isMirrored ? -xform.rotationDegrees : xform.rotationDegrees;

  if (Math.abs(actualRotation) > ANGLE_EPSILON) {
    const rads = degreeToRadian(actualRotation);
    const s = Math.sin(rads);
    const c = Math.cos(rads);

    for (let i = uvsStartIndex, l = uvsEndIndex; i < l; i += 2) {
      const u = uvs[i];
      const v = uvs[i + 1];

      uvs[i] = u * c - v * s;
      uvs[i + 1] = u * s + v * c;
    }
    planeRotateAroundNormal(uvPlane, -rads);
  }

  return uvPlane;
}
